import type { CatalogCategory, CatalogProduct } from '@horevia/contracts';
import {
  Badge,
  Button,
  CatalogLayout,
  CategoryList,
  CategoryRow,
  CheckboxField,
  Container,
  EmptyState,
  ErrorState,
  Input,
  NoPermissionState,
  PageHeader,
  ProductCard,
  ProductGrid,
  Skeleton,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { LayoutGrid, Pencil, Plus, Search, Utensils } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { CategoryDrawer, ProductDrawer } from '@/features/pos/CatalogDrawers';
import { RecipeDrawer } from '@/features/stock/RecipeDrawer';
import { useCurrentOutlet } from '@/features/pos/outlet';
import {
  categoriesQueryOptions,
  destinationsQueryOptions,
  productsQueryOptions,
} from '@/features/pos/queries';
import { categoryIcon } from '@/features/pos/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount } from '@/lib/format';
import { useCurrentProperty } from '@/lib/property';

export const Route = createFileRoute('/_app/pos/catalog')({ component: CatalogPage });

/** Administration de la carte (charte §9 : colonne des catégories + grille de produits). */
function CatalogPage() {
  const { t, i18n } = useTranslation(['pos', 'stock', 'common', 'errors']);
  const session = useSession();
  const { property } = useCurrentProperty();
  const { outlets, outlet } = useCurrentOutlet();
  const canRead = session.can('catalog.read');
  const canManage = session.can('catalog.manage');
  const [categoryId, setCategoryId] = useState<string>('ALL');
  const [term, setTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [categoryDrawer, setCategoryDrawer] = useState<{
    open: boolean;
    category: CatalogCategory | null;
  }>({ open: false, category: null });
  const [productDrawer, setProductDrawer] = useState<{
    open: boolean;
    product: CatalogProduct | null;
  }>({ open: false, product: null });
  // Recette de déstockage (Horevia Inventory, ADR-020) : visible si le module est actif.
  const canRecipe = session.hasModule('inventory') && session.can('stock.read');
  const [recipeDrawer, setRecipeDrawer] = useState<{
    open: boolean;
    product: CatalogProduct | null;
  }>({ open: false, product: null });

  const categories = useQuery(
    categoriesQueryOptions(
      { propertyId: property?.id, includeInactive: showInactive },
      canRead && Boolean(property),
    ),
  );
  const products = useQuery(
    productsQueryOptions(
      { propertyId: property?.id, includeInactive: showInactive },
      canRead && Boolean(property),
    ),
  );
  const destinations = useQuery(
    destinationsQueryOptions(outlet?.id ?? '', canRead && Boolean(outlet)),
  );

  const visible = useMemo(() => {
    const list = products.data ?? [];
    const filtered =
      categoryId === 'ALL' ? list : list.filter((product) => product.categoryId === categoryId);
    const needle = term.trim().toLowerCase();
    return needle
      ? filtered.filter(
          (p) => p.name.toLowerCase().includes(needle) || p.code.toLowerCase().includes(needle),
        )
      : filtered;
  }, [products.data, categoryId, term]);
  const categoryById = useMemo(
    () => new Map((categories.data ?? []).map((c) => [c.id, c])),
    [categories.data],
  );

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('pos:catalog.title')}
        subtitle={t('pos:catalog.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('pos:catalog.title') },
        ]}
        actions={
          canManage ? (
            <div className="row row--wrap" style={{ gap: 8 }}>
              <Button
                variant="secondary"
                onClick={() => setCategoryDrawer({ open: true, category: null })}
              >
                <Plus className="ico" aria-hidden="true" />
                {t('pos:catalog.newCategory')}
              </Button>
              <Button
                variant="primary"
                disabled={(categories.data ?? []).length === 0}
                onClick={() => setProductDrawer({ open: true, product: null })}
              >
                <Plus className="ico" aria-hidden="true" />
                {t('pos:catalog.newProduct')}
              </Button>
            </div>
          ) : undefined
        }
      />

      {categories.isError || products.isError ? (
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(categories.error ?? products.error, t)}
          traceId={errorTraceId(categories.error ?? products.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => {
            void categories.refetch();
            void products.refetch();
          }}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      ) : (
        <CatalogLayout>
          <CategoryList aria-label={t('pos:catalog.allCategories')}>
            <CategoryRow
              active={categoryId === 'ALL'}
              icon={<LayoutGrid className="ico ico-16" aria-hidden="true" />}
              color="SLATE"
              count={products.data?.length}
              onClick={() => setCategoryId('ALL')}
            >
              {t('pos:catalog.allCategories')}
            </CategoryRow>
            {(categories.data ?? []).map((category) => {
              const Icon = categoryIcon(category.icon);
              return (
                <CategoryRow
                  key={category.id}
                  active={categoryId === category.id}
                  icon={<Icon className="ico ico-16" aria-hidden="true" />}
                  color={category.color}
                  count={category.productsCount}
                  inactive={!category.isActive}
                  onClick={() => setCategoryId(category.id)}
                  onDoubleClick={() => canManage && setCategoryDrawer({ open: true, category })}
                >
                  {category.name}
                </CategoryRow>
              );
            })}
            <CheckboxField
              label={t('pos:catalog.showInactive')}
              checked={showInactive}
              onCheckedChange={(value) => setShowInactive(value === true)}
            />
          </CategoryList>

          <div className="stack">
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <Input
                aria-label={t('pos:catalog.search')}
                placeholder={t('pos:catalog.search')}
                icon={<Search className="ico" aria-hidden="true" />}
                value={term}
                onChange={(event) => setTerm(event.target.value)}
              />
              {canManage && categoryId !== 'ALL' && categoryById.get(categoryId) ? (
                <Button
                  variant="secondary"
                  onClick={() =>
                    setCategoryDrawer({
                      open: true,
                      category: categoryById.get(categoryId) ?? null,
                    })
                  }
                >
                  <Pencil className="ico" aria-hidden="true" />
                  {t('pos:catalog.editCategory')}
                </Button>
              ) : null}
            </div>
            {products.isPending ? (
              <Skeleton height={240} />
            ) : visible.length === 0 ? (
              <EmptyState
                icon={<Utensils className="ico" aria-hidden="true" />}
                title={t('pos:catalog.empty')}
                description={t('pos:catalog.emptyHint')}
              />
            ) : (
              <ProductGrid>
                {visible.map((product) => {
                  const category = categoryById.get(product.categoryId);
                  const Icon = categoryIcon(category?.icon ?? 'utensils');
                  return (
                    <ProductCard
                      key={product.id}
                      name={product.name}
                      category={`${product.code}${category ? ` · ${category.name}` : ''}`}
                      price={formatAmount(product.price, i18n.language, product.currencyCode)}
                      icon={<Icon className="ico" aria-hidden="true" />}
                      color={category?.color ?? 'GREEN'}
                      aria-label={`${t('pos:catalog.editProduct')} — ${product.name}`}
                      onClick={() => canManage && setProductDrawer({ open: true, product })}
                      badges={
                        <>
                          {!product.isActive ? (
                            <Badge tone="neutral">{t('pos:catalog.inactive')}</Badge>
                          ) : null}
                          {product.isActive && !product.isAvailable ? (
                            <Badge tone="red">{t('pos:catalog.unavailable')}</Badge>
                          ) : null}
                        </>
                      }
                      control={
                        product.optionGroups.length > 0 || canRecipe ? (
                          <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                            {product.optionGroups.length > 0 ? (
                              <span className="hint">
                                {t('pos:catalog.options', { count: product.optionGroups.length })}
                              </span>
                            ) : null}
                            {canRecipe ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                aria-label={`${t('stock:recipe.button')} — ${product.name}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setRecipeDrawer({ open: true, product });
                                }}
                              >
                                {t('stock:recipe.button')}
                              </Button>
                            ) : null}
                          </span>
                        ) : undefined
                      }
                    />
                  );
                })}
              </ProductGrid>
            )}
          </div>
        </CatalogLayout>
      )}

      {property ? (
        <>
          {canRecipe ? (
            <RecipeDrawer
              open={recipeDrawer.open}
              onOpenChange={(open) => setRecipeDrawer((current) => ({ ...current, open }))}
              product={recipeDrawer.product}
              canManage={session.can('stock.manage')}
            />
          ) : null}
          <CategoryDrawer
            open={categoryDrawer.open}
            onOpenChange={(open) => setCategoryDrawer((current) => ({ ...current, open }))}
            propertyId={property.id}
            outlets={outlets}
            destinations={destinations.data ?? []}
            category={categoryDrawer.category}
          />
          <ProductDrawer
            open={productDrawer.open}
            onOpenChange={(open) => setProductDrawer((current) => ({ ...current, open }))}
            categories={(categories.data ?? []).filter((category) => category.isActive)}
            destinations={destinations.data ?? []}
            defaultCategoryId={categoryId === 'ALL' ? undefined : categoryId}
            product={productDrawer.product}
          />
        </>
      ) : null}
    </Container>
  );
}
